import React from 'react';
import { Phone, Mail, MapPin, Linkedin, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const quickLinks = [
    { name: 'Home', id: 'home' },
    { name: 'About', id: 'about' },
    { name: 'Services', id: 'services' },
    { name: 'Client Stories', id: 'client-stories' },
    { name: 'Contact', id: 'contact' }
  ];

  const services = [
    { name: 'Direct Taxation', id: 'direct-taxation' },
    { name: 'GST Services', id: 'indirect-taxation-gst' },
    { name: 'Audit & Assurance', id: 'audit-assurance' },
    { name: 'Business Setup', id: 'business-setup' },
    { name: 'Corporate Law', id: 'corporate-law' },
    { name: 'Accounting Services', id: 'accounting-bookkeeping' }
  ];

  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4">
              <span className="text-teal-400">Pankaj R Goyal & Associates</span>
            </h3>
            <p className="text-slate-400 mb-4 leading-relaxed">
              Professional Chartered Accountant providing comprehensive financial services with dedication and expertise.
            </p>
            <div className="mt-4 flex items-center gap-4 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_emergent-ca-build/artifacts/r9svxsnb_WhatsApp%20Image%202026-02-15%20at%2013.09.41.jpeg"
                alt="CA Pankaj Goyal"
                className="w-20 h-20 rounded-lg object-cover border-2 border-teal-500 shadow-lg"
              />
              <div>
                <p className="text-white font-semibold text-lg">Pankaj Goyal</p>
                <p className="text-slate-400 text-sm">Chartered Accountant</p>
              </div>
            </div>
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/in/ca-pankaj-goyal2024"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 p-2 rounded-lg hover:bg-teal-600 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/pankaj_r_goyal?igsh=MWVyZ3g5NTE4cnJ4"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 p-2 rounded-lg hover:bg-teal-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-slate-400 hover:text-teal-400 transition-colors"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(`/services/${service.id}`)}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-left"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                <a href="tel:9636557944" className="text-slate-400 hover:text-teal-400 transition-colors">
                  +91 9636557944
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                <a href="mailto:pankaj@pankajrgoyal.com" className="text-slate-400 hover:text-teal-400 transition-colors break-all">
                  pankaj@pankajrgoyal.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">India</span>
              </li>
            </ul>
            
            {/* Social Links in Contact Section */}
            <div className="mt-6">
              <h5 className="text-sm font-semibold mb-3 text-slate-300">Follow Us</h5>
              <div className="flex gap-3">
                <a
                  href="https://www.linkedin.com/in/ca-pankaj-goyal2024"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-teal-400 transition-colors flex items-center gap-2 text-sm"
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
                <a
                  href="https://www.instagram.com/pankaj_r_goyal?igsh=MWVyZ3g5NTE4cnJ4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-teal-400 transition-colors flex items-center gap-2 text-sm"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              © {currentYear} Pankaj R Goyal & Associates - Chartered Accountant. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <button className="text-slate-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </button>
              <button className="text-slate-400 hover:text-teal-400 transition-colors">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
